import { all,
  always,
  append,
  compose,
  cond,
  defaultTo,
  equals,
  filter,
  has,
  includes,
  is,
  isNil,
  join,
  map,
  mergeDeepRight,
  path,
  prop,
  propOr,
  reject,
  split,
  take,
  when } from 'ramda';
import { isNilOrEmpty, propNotEq } from 'ramda-adjunct';
import React, { useContext, useEffect, useState } from 'react';
import treeUtil from 'tree-util';
import { customSuccessMessage, errorMessage } from '../../utils/messageMutation';
import CreateFolderMutation from '../../_graphql/mutations/folders/CreateFolderMutation';
import UpdateFolderMutation from '../../_graphql/mutations/folders/UpdateFolderMutation';
import RemoveFoldersMutation from '../../_graphql/mutations/folders/RemoveFoldersMutation';
import useEffectMounted from '../../hooks/useEffectMounted';
import useActionGuard from '../../hooks/useActionGuard';
import { addAllowedAccessFolders,
  addKeys,
  appendElementToFolder,
  cleanFolders,
  collectOutsideKeys,
  setDefaultPropsToElement,
  updateKeys } from './treeHelper';

const defaultValue = { data: [] };
const standardConfig = { id: 'id', parentid: 'parent' };

export const TreeContext = React.createContext(defaultValue);

export const useInternalTreeContext = ({ folders: _folders, reFetch, reFetchElement, args, elements, userCanEditOffer, userCanCreateOfferDocument }) => {
  const { offerId, scope, type } = args;
  const [folders, setFolders] = useState([]);
  const [trees, setTrees] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState(() => {
    const savedExpandedKeys = localStorage.getItem(`${type}-${offerId}-expandedKeys`);
    return savedExpandedKeys ? JSON.parse(savedExpandedKeys) : [];
  });
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [allOutsideFolders, setAllOutsideFolders] = useState(false);
  const { loading, run } = useActionGuard();

  useEffect(() => {
    localStorage.setItem(`${type}-${offerId}-expandedKeys`, JSON.stringify(expandedKeys));
    setAutoExpandParent(false);
  }, [expandedKeys, offerId, type]);

  useEffectMounted((mounted) => {
    if (mounted) setFolders(_folders);
  }, [_folders]);

  useEffectMounted((mounted) => {
    const dataElmIds = compose(reject(isNil), map(path(['element', 'elmId'])))(folders);
    const elms = compose(
      map(elm => ({ ...elm, elmId: prop('id', elm) })),
      map(setDefaultPropsToElement),
      reject(w => includes(prop('id', w), dataElmIds)),
      defaultTo([])
    )(elements);

    const foldersTree = compose(
      when(isNilOrEmpty, always([])),
      appendElementToFolder(elements),
      addAllowedAccessFolders(userCanEditOffer || userCanCreateOfferDocument)
    )(folders);

    const trees = compose(
      addKeys('0', 'key'),
      cleanFolders,
      map(propOr([], 'rootNode'))
    )(treeUtil.buildTrees([...elms, ...foldersTree], standardConfig));
    if (mounted) setTrees(trees);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folders, elements]);

  const handleResponse = (updatedKeys, type, fnNewData, cb, isMultiple) => (ok, error) => {
    if (!ok) {
      errorMessage();
      return;
    }
    const msg = cond([
      [equals('creation'), always('Création réussie')],
      [equals('update'), always('Modification réussie')],
      [equals('move'), always('L\'élément a été déplacé')],
      [equals('remove'), always('Le dossier a été supprimé')]
    ])(type);
    if (!isMultiple) customSuccessMessage(msg);
    setExpandedKeys(updatedKeys);
    setCheckedKeys([]);
    reFetch();
    if (fnNewData) setFolders(fnNewData);
    if (is(Function, cb)) cb(ok, error);
  };

  const addTree = (parentId = null, updatedKeys, isMultiple) => (data = {}) => {
    const newTree = mergeDeepRight({
      name: 'Nouveau dossier',
      teamId: null,
      offerId,
      parent: parentId,
      scope,
      type
    }, data);

    run(`create-folder-${parentId}`, (done) => CreateFolderMutation({ folder: newTree }, done), handleResponse(updatedKeys, 'creation', append(newTree), null, isMultiple));
  };

  const handleUpdateFolderMutation = (parentId, data, updatedKeys, isMoved, isMultiple) => {
    run(`update-folder-${parentId}`, (done) => UpdateFolderMutation({
      id: parentId,
      folder: { ...data }
    }, done), handleResponse(updatedKeys, isMoved ? 'move' : 'update', null, null, isMultiple));
  };
  const updateTree = (parentId, updatedKeys, isMultiple) => (data) => {
    const isMoved = has('parent', data);
    handleUpdateFolderMutation(parentId, data, updatedKeys, isMoved, isMultiple);
  };

  const handleRemoveFoldersMutation = (foldersIds, updatedKeys, type, isMultiple) => {
    run(`remove-folders-${foldersIds.join('-')}`, (done) => RemoveFoldersMutation({ foldersIds }, done), handleResponse(updatedKeys, type, filter(propNotEq(foldersIds, 'id')), null, isMultiple));
  };

  const removeTree = (foldersIds, folder, isFile, isMultiple) => {
    const nodeKey = prop('key', folder);
    if (isFile) {
      const updatedKeys = updateKeys(expandedKeys, [nodeKey], 'move');
      handleRemoveFoldersMutation(foldersIds, updatedKeys, 'move', isMultiple);
    } else {
      const updatedKeys = updateKeys(expandedKeys, [nodeKey], 'remove');
      handleRemoveFoldersMutation(foldersIds, updatedKeys, 'remove', isMultiple);
    }
  };

  const getElement = folder => ({ ...prop('element', folder), key: prop('key', folder) });
  const onExpand = (keys) => {
    const parentKeys = map(key => join('-', take(2, split('-', key))), keys);
    const allParentsPresent = all(parent => includes(parent, keys), parentKeys);
    const updatedKeysWithParent = allParentsPresent ? keys : filter(key => includes(key, parentKeys))(keys);
    setExpandedKeys(updatedKeysWithParent);
    setAutoExpandParent(false);
  };

  const selectAllOutsideFolders = (isChecked) => {
    setAllOutsideFolders(isChecked);
    const outsideKeys = isChecked ? collectOutsideKeys(trees) : [];
    setCheckedKeys(outsideKeys);
  };

  const updateSelectAllOutsideFolders = () => {
    const outsideKeys = collectOutsideKeys(trees);

    if (isNilOrEmpty(trees) || isNilOrEmpty(outsideKeys)) {
      setAllOutsideFolders(false);
      return false;
    }

    const allSelected = all((key) => includes(key, checkedKeys), outsideKeys);
    const noneSelected = all((key) => !includes(key, checkedKeys), outsideKeys);
    setAllOutsideFolders(allSelected);
    return !allSelected && !noneSelected;
  };

  return {
    trees,
    loading,
    addTree,
    updateTree,
    removeTree,
    getElement,
    reFetchElement,
    reFetchFolder: reFetch,
    args,
    expandedKeys,
    setExpandedKeys,
    autoExpandParent,
    setAutoExpandParent,
    checkedKeys,
    setCheckedKeys,
    onExpand,
    selectAllOutsideFolders,
    allOutsideFolders,
    updateSelectAllOutsideFolders
  };
};

export const useTreeContext = () => useContext(TreeContext);

export const TreeProvider = ({ children, args, reFetch: reFetchElement, folders, reFetchFolders, ...restProps }) => {
  const context = useInternalTreeContext({
    args, folders, reFetch: reFetchFolders, reFetchElement, ...restProps
  });
  return (
    <TreeContext.Provider value={context}>
      {/* NE PAS TOUCHER */}
      {children && children}
    </TreeContext.Provider>
  );
};
